/**
 * Renders an account's assigned broker as a hyperlink to the broker detail page
 * (`/brokers/:id`).
 *
 * Decision: in the Accounts view, every reference to the assigned broker is a
 * hyperlink so users can jump straight to the broker's profile (see the
 * client-components AGENTS.md "Accounts" note).
 *
 * The `/brokers/:id` route only exists in the client/internal-admin route group;
 * the broker portal route group has no such page, so for broker users we fall
 * back to plain text rather than producing a dead link.
 */

import type {ReactElement} from 'react';
import {Link} from 'react-router-dom';
import {useAuthStore} from '@common-web/stores/authStore';
import {cn} from '@common-web/lib/utils';

interface AssignedBrokerLinkProps {
  broker: {id: string; name: string} | null;
  /** Classes applied to the rendered name (link or fallback text). */
  className?: string;
  /** Label shown when no broker is assigned. */
  unassignedLabel?: string;
  /**
   * Stop click propagation so the link works inside a clickable container
   * (e.g. a kanban card or table row) without also triggering its navigation.
   */
  stopPropagation?: boolean;
}

export function AssignedBrokerLink({
  broker,
  className,
  unassignedLabel = 'Unassigned',
  stopPropagation = false,
}: AssignedBrokerLinkProps): ReactElement {
  const isBrokerUser = useAuthStore((state) => state.isBrokerUser());

  if (!broker) {
    return <span className={cn('text-muted-foreground', className)}>{unassignedLabel}</span>;
  }

  if (isBrokerUser) {
    return <span className={className}>{broker.name}</span>;
  }

  return (
    <Link
      to={`/brokers/${broker.id}`}
      onClick={stopPropagation ? (e) => e.stopPropagation() : undefined}
      className={cn('text-primary hover:underline', className)}
    >
      {broker.name}
    </Link>
  );
}
